import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import './Approvals.css';
import './Customers.css';

const TYPES = ['MSA', 'SOW', 'Engagement', 'Invoice', 'Timesheet'];

function entityPath(item) {
  switch (item.entity_type) {
    case 'MSA': return `/msa/${item.entity_id}`;
    case 'SOW': return `/sow/${item.entity_id}`;
    case 'Timesheet': return `/timesheets/${item.entity_id}`;
    case 'Engagement': return `/engagements/${item.entity_id}`;
    default: return `/invoices/${item.entity_id}`;
  }
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ApproveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function RejectIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
    </svg>
  );
}

export default function Approvals() {
  const [pending, setPending] = useState([]);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filterType, setFilterType] = useState('MSA');
  const [search, setSearch] = useState('');

  const load = () => api.get('/approvals/pending').then(setPending);
  useEffect(() => { load(); }, []);

  const handleApprove = async (item) => {
    if (!window.confirm(`Approve ${item.entity_type} ${item.entity_id}?`)) return;
    await api.post('/approvals/approve', { entity_type: item.entity_type, entity_id: item.entity_id });
    load();
  };

  const handleReject = async () => {
    await api.post('/approvals/reject', { entity_type: rejectModal.entity_type, entity_id: rejectModal.entity_id, comments: rejectReason });
    setRejectModal(null);
    setRejectReason('');
    load();
  };

  const summary = useMemo(() => {
    const counts = {};
    TYPES.forEach(t => { counts[t] = 0; });
    pending.forEach(p => { counts[p.entity_type] = (counts[p.entity_type] || 0) + 1; });
    return counts;
  }, [pending]);

  const filtered = useMemo(() => {
    return pending.filter(p => {
      if (filterType && p.entity_type !== filterType) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return [p.entity_id, p.ref, p.client_name, p.submitted_by_name, p.entity_type]
        .some(v => (v || '').toString().toLowerCase().includes(q));
    });
  }, [pending, filterType, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(p => { (g[p.entity_type] = g[p.entity_type] || []).push(p); });
    return g;
  }, [filtered]);

  const totalPending = pending.length;

  return (
    <>
      <div className="page-header">
        <h1>Pending Approvals</h1>
      </div>
      <div className="page-content approvals-page">
        <div className="customers-top approvals-stats-top">
          <div className="customers-stats" aria-label="Pending approval counts">
            <div className="customers-stat">
              <div className="customers-stat-label">Total pending</div>
              <div className="customers-stat-value">{totalPending.toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">{filterType}</div>
              <div className="customers-stat-value">{(summary[filterType] || 0).toLocaleString()}</div>
            </div>
            <div className="customers-stat">
              <div className="customers-stat-label">Match search</div>
              <div className="customers-stat-value">{filtered.length.toLocaleString()}</div>
            </div>
          </div>
          <div className="customers-top-right">
            {search.trim() ? (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setSearch('')}>
                Clear search
              </button>
            ) : null}
          </div>
        </div>

        <div className="approvals-tab-strip" role="tablist" aria-label="Approval document type">
          {TYPES.map(t => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={filterType === t}
              className={`tab approvals-tab ${filterType === t ? 'tab-active' : ''}`}
              onClick={() => setFilterType(t)}
            >
              {t}
              <span className="approvals-tab-count">{summary[t] || 0}</span>
            </button>
          ))}
        </div>

        <div className="approvals-toolbar">
          <div className="approvals-search-wrap">
            <SearchIcon />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by ID, reference, client, submitter…"
              aria-label="Search pending approvals"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="card approvals-empty">
            <div className="approvals-empty-icon" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            <h3>No pending approvals</h3>
            <p>
              {totalPending === 0
                ? 'There are no submitted items waiting for approval right now.'
                : 'Nothing matches your search or the selected document type. Try another tab or clear the search.'}
            </p>
          </div>
        ) : (
          TYPES.filter(t => grouped[t]?.length).map(t => (
            <div className="card approvals-table-card" key={t}>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Reference</th>
                      <th>Client</th>
                      <th>Submitted By</th>
                      <th>Submitted At</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[t].map((item, i) => (
                      <tr key={`${item.entity_type}-${item.entity_id}-${i}`}>
                        <td>
                          <Link to={entityPath(item)} className="link tabular-nums">{item.entity_id}</Link>
                        </td>
                        <td>{item.ref}</td>
                        <td>{item.client_name}</td>
                        <td>{item.submitted_by_name}</td>
                        <td>{item.submitted_at}</td>
                        <td className="text-right">
                          <div className="approvals-row-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm approvals-action-icon"
                              aria-label={`Approve ${item.entity_type} ${item.entity_id}`}
                              title="Approve"
                              onClick={() => handleApprove(item)}
                            >
                              <ApproveIcon />
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline btn-sm approvals-btn-reject approvals-action-icon"
                              aria-label={`Reject ${item.entity_type} ${item.entity_id}`}
                              title="Reject"
                              onClick={() => { setRejectModal(item); setRejectReason(''); }}
                            >
                              <RejectIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Reject {rejectModal.entity_type} {rejectModal.entity_id}</h2>
            <div className="form-group">
              <label>Reason for rejection</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={4} placeholder="Please provide a reason…" />
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={handleReject} disabled={!rejectReason.trim()}>Confirm reject</button>
              <button type="button" className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
